import { Play, Quote, Star } from "lucide-react";

// Placeholder media — easy to swap after the free Build Day event is filmed.
const TILES = [
  {
    type: "image",
    url: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    label: "Workshop Footage",
    span: "md:col-span-3 md:row-span-2",
  },
  {
    type: "video",
    url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    label: "Speaking Clip",
    span: "md:col-span-3",
  },
  {
    type: "image",
    url: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    label: "Networking",
    span: "md:col-span-3",
  },
];

const TESTIMONIALS = [
  {
    name: "Calum Rob",
    role: "Director",
    quote:
      "Walked in unsure, walked out with a clearer offer and a 30-day plan I actually trust. The Sprint shifts something — fast.",
  },
  {
    name: "Teekay Films",
    role: "Videographer",
    quote:
      "The content session alone gave me a month of assets. The room was full of people actually building — that energy changes you.",
  },
];

export const BuildDayGallery = () => {
  return (
    <section
      id="gallery-buildday"
      data-testid="buildday-gallery-section"
      className="relative bg-[#0E0F12] py-24 md:py-40 border-t border-zinc-800"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="md:col-span-7">
            <div className="reveal flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#FCD34D]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#FCD34D] font-bold">
                Inside The Room
              </span>
            </div>
            <h2
              data-testid="buildday-gallery-headline"
              className="reveal text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-extrabold text-white leading-[1.02]"
            >
              From the studio floor.{" "}
              <span className="italic font-light text-white">Receipts only.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex md:items-end">
            <p className="reveal text-base md:text-lg leading-relaxed text-zinc-400 font-medium">
              Footage and reactions from inside The Builders Sprint.
              Placeholders today — replaced with real moments after the next
              event.
            </p>
          </div>
        </div>

        {/* Image / video bento */}
        <div className="grid grid-cols-2 md:grid-cols-6 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4 mb-6 md:mb-8">
          {TILES.map((tile, i) => (
            <div
              key={i}
              data-testid={`buildday-gallery-tile-${i + 1}`}
              className={`reveal group relative overflow-hidden bg-[#15161B] border border-zinc-800 ${tile.span}`}
            >
              <img
                src={tile.url}
                alt={tile.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

              {tile.type === "video" && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 border border-white/40 bg-black/40 backdrop-blur-sm group-hover:bg-[#4F7CFF]/30 group-hover:border-[#4F7CFF] transition-all duration-300">
                    <Play
                      className="w-5 h-5 md:w-6 md:h-6 text-white ml-0.5"
                      strokeWidth={1.5}
                      fill="currentColor"
                    />
                  </span>
                </div>
              )}

              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 flex items-end justify-between">
                <span className="text-xs tracking-[0.25em] uppercase text-white font-bold">
                  {tile.label}
                </span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              data-testid={`buildday-testimonial-${i + 1}`}
              className="reveal group bg-[#15161B] hover:bg-[#1A1B22] transition-colors duration-500 p-8 md:p-10 flex flex-col"
            >
              <Quote
                className="w-6 h-6 text-zinc-600 group-hover:text-[#4F7CFF] transition-colors duration-300 mb-6"
                strokeWidth={1.3}
              />
              <blockquote className="text-base md:text-lg leading-relaxed text-zinc-200 font-medium mb-8">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-auto flex items-end justify-between border-t border-zinc-900 pt-5">
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star
                        key={s}
                        className="w-3.5 h-3.5 text-white fill-white"
                        strokeWidth={1.2}
                      />
                    ))}
                  </div>
                  <div className="text-white font-bold tracking-tight">{t.name}</div>
                  <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 font-bold mt-1">
                    {t.role}
                  </div>
                </div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                  The Sprint
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};
